/**
 * DataManager — Centralized data access singleton.
 * Replaces in-memory-only state with REST calls to the Container backend.
 * Caches data locally for fast reads, syncs writes to D1/R2.
 *
 * Usage:
 *   await dataManager.init()    — Loads user + workers + conversation previews
 *   dataManager.getWorkers()    — Returns cached workers
 *   dataManager.getConversation(deskIndex) — Fetches from R2 via API
 */

import { authManager } from '@/auth/auth-manager';
import type {
  UserData, UserRecord, WorkerRecord,
  ConversationMessage, ConversationPreview, GreetingResponse,
  TaskRecord, DeliverableRecord, DeliverableContent, TaskPlan,
} from './types';

class DataManager {
  private _user: UserRecord | null = null;
  private _workers: WorkerRecord[] = [];
  private _conversationPreviews: Record<number, ConversationPreview> = {};
  private _conversationCache: Map<number, ConversationMessage[]> = new Map();
  private _initialized = false;
  private baseUrl = '/api';

  // ── Init ──────────────────────────────────────────────────────

  /**
   * Load user data from the backend on app boot.
   * Creates user + default workers on first login.
   */
  async init(): Promise<UserData> {
    const data = await this.fetchJSON<UserData & { conversationPreviews: Record<number, ConversationPreview> }>(
      '/data/user',
    );

    this._user = data.user;
    this._workers = data.workers;
    this._conversationPreviews = data.conversationPreviews || {};
    this._initialized = true;

    return data;
  }

  get initialized(): boolean { return this._initialized; }

  // ── User ──────────────────────────────────────────────────────

  get user(): UserRecord | null { return this._user; }
  get isOnboardingComplete(): boolean { return this._user?.onboarding_complete === 1; }

  async setOnboardingComplete(): Promise<void> {
    await this.fetchJSON('/data/user/onboarding', { method: 'PUT' });
    if (this._user) this._user.onboarding_complete = 1;
  }

  // ── Workers ───────────────────────────────────────────────────

  get workers(): WorkerRecord[] { return this._workers; }

  getWorkerName(deskIndex: number): string {
    const w = this._workers.find(w => w.desk_index === deskIndex);
    return w?.name || `Worker ${deskIndex + 1}`;
  }

  getWorkerRole(deskIndex: number): string {
    const w = this._workers.find(w => w.desk_index === deskIndex);
    return w?.role || 'Worker';
  }

  getWorkerNames(): string[] {
    return this._workers
      .sort((a, b) => a.desk_index - b.desk_index)
      .map(w => w.name);
  }

  getWorkerRoles(): string[] {
    return this._workers
      .sort((a, b) => a.desk_index - b.desk_index)
      .map(w => w.role);
  }

  // ── Conversations ─────────────────────────────────────────────

  /**
   * Load conversation history for a specific worker.
   * Caches in memory after first load.
   */
  async getConversation(deskIndex: number, forceRefresh = false): Promise<ConversationMessage[]> {
    if (!forceRefresh && this._conversationCache.has(deskIndex)) {
      return this._conversationCache.get(deskIndex)!;
    }

    const data = await this.fetchJSON<{
      messages: ConversationMessage[];
      total: number;
      hasMore: boolean;
    }>(`/data/conversations/${deskIndex}`);

    this._conversationCache.set(deskIndex, data.messages);
    return data.messages;
  }

  /**
   * Add a message to the local cache (for immediate UI update).
   * The actual save to R2 happens when the chat API is called.
   */
  addLocalMessage(deskIndex: number, msg: ConversationMessage): void {
    if (!this._conversationCache.has(deskIndex)) {
      this._conversationCache.set(deskIndex, []);
    }
    this._conversationCache.get(deskIndex)!.push(msg);
  }

  getConversationPreview(deskIndex: number): ConversationPreview | undefined {
    return this._conversationPreviews[deskIndex];
  }

  // ── Tasks ────────────────────────────────────────────────────

  async listTasks(filters?: { status?: string; workerId?: string }): Promise<TaskRecord[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.workerId) params.set('workerId', filters.workerId);
    const query = params.toString() ? `?${params.toString()}` : '';
    const data = await this.fetchJSON<{ tasks: TaskRecord[] }>(`/tasks${query}`);
    return data.tasks;
  }

  async executePlan(plan: TaskPlan): Promise<{ planId: string; tasks: TaskRecord[] }> {
    return this.fetchJSON<{ planId: string; tasks: TaskRecord[] }>('/tasks/execute', {
      method: 'POST',
      body: plan,
    });
  }

  // ── Deliverables ────────────────────────────────────────────

  private _deliverableCache: Map<string, DeliverableContent> = new Map();

  async listDeliverables(workerId?: string): Promise<DeliverableRecord[]> {
    const query = workerId ? `?workerId=${encodeURIComponent(workerId)}` : '';
    const data = await this.fetchJSON<{ deliverables: DeliverableRecord[] }>(`/deliverables${query}`);
    return data.deliverables;
  }

  async getDeliverableContent(id: string): Promise<DeliverableContent> {
    if (this._deliverableCache.has(id)) {
      return this._deliverableCache.get(id)!;
    }
    const content = await this.fetchJSON<DeliverableContent>(`/deliverables/${encodeURIComponent(id)}`);
    this._deliverableCache.set(id, content);
    return content;
  }

  // ── Greeting ──────────────────────────────────────────────────

  async getGreeting(): Promise<GreetingResponse> {
    return this.fetchJSON<GreetingResponse>('/data/greeting');
  }

  // ── Fetch Helper ──────────────────────────────────────────────

  private async fetchJSON<T>(
    path: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...authManager.getAuthHeaders(),
    };

    const fetchOptions: RequestInit = {
      method: options.method || 'GET',
      headers,
    };

    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body);
    }

    const res = await fetch(`${this.baseUrl}${path}`, fetchOptions);

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`API error ${res.status}: ${errorText}`);
    }

    return res.json() as Promise<T>;
  }
}

// Singleton export
export const dataManager = new DataManager();
